import { useCallback, useEffect, useRef, useState } from "react";

// Voice for aulasAI, using only what the browser / device already has (free, nothing on our servers):
// - voice typing: the Web Speech API's recognition (Chrome, Edge, Safari). The words go into the message box and
//   are never sent by themselves; the user reads them and presses Send.
// - read aloud: the device's own voices (speechSynthesis), only when the user taps the speaker on a reply.
// English only for now: Hindi spoken in an English (India) session comes out in Roman letters, i.e. Hinglish.

const LANG = "en-IN";
const Recognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export const NOT_SUPPORTED_TYPING = "Voice typing isn't available in this browser. Try Chrome, Edge or Safari.";
export const NOT_SUPPORTED_READING = "Reading aloud isn't available in this browser.";

const RECOGNITION_ERRORS = {
  "not-allowed": "Microphone access is blocked. Allow it for this site in your browser settings, then try again.",
  "service-not-allowed": "Microphone access is blocked. Allow it for this site in your browser settings, then try again.",
  "audio-capture": "No microphone was found. Check that one is connected.",
  "no-speech": "I didn't hear anything. Tap the mic and try again.",
  // Brave and some others expose the API but have no speech service behind it
  network: NOT_SUPPORTED_TYPING,
  "language-not-supported": NOT_SUPPORTED_TYPING,
};

// Voice typing. `onText(text)` gets the words heard so far, again with each new word.
export function useDictation(onText) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");
  const recognitionRef = useRef(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
  }, []);

  const start = useCallback(() => {
    setError("");
    if (!Recognition) {
      setError(NOT_SUPPORTED_TYPING);
      return;
    }
    if (recognitionRef.current) return;
    const recognition = new Recognition();
    recognition.lang = LANG;
    recognition.interimResults = true; // show the words while the user is still speaking
    recognition.continuous = false; // stops by itself after a pause
    recognition.onresult = (event) => {
      let text = "";
      for (let i = 0; i < event.results.length; i += 1) text += event.results[i][0].transcript;
      onTextRef.current(text.trim());
    };
    recognition.onerror = (event) => {
      if (event.error !== "aborted") setError(RECOGNITION_ERRORS[event.error] || "Voice typing stopped. Please try again.");
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch (e) {
      recognitionRef.current = null;
      setError(NOT_SUPPORTED_TYPING);
    }
  }, []);

  useEffect(() => () => recognitionRef.current && recognitionRef.current.abort(), []);

  const clearError = useCallback(() => setError(""), []);
  return { listening, error, clearError, start, stop };
}

// Short pieces, sentence by sentence: Chrome cuts off long utterances, and stopping is quicker.
const pieces = (text) => {
  const clean = text
    .replace(/https?:\/\/\S+/g, "") // links are buttons, not something to read out
    .replace(/^\s*[•◦-]\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  const sentences = clean.match(/[^.!?]+[.!?]*\s*/g) || [];
  const out = [];
  sentences.forEach((s) => {
    const last = out[out.length - 1];
    if (last && last.length + s.length < 180) out[out.length - 1] = last + s;
    else out.push(s);
  });
  return out.map((s) => s.trim()).filter(Boolean);
};

// The device's own English voice, preferring one that runs on the device (nothing sent anywhere) and Indian English.
const pickVoice = () => {
  const voices = window.speechSynthesis.getVoices();
  const english = voices.filter((v) => /^en/i.test(v.lang));
  return english.find((v) => v.localService && /en[-_]IN/i.test(v.lang))
    || english.find((v) => v.localService)
    || english.find((v) => /en[-_]IN/i.test(v.lang))
    || english[0]
    || null;
};

// Read aloud. One reply at a time: `speaking` is the key of the reply being read.
export function useSpeaker() {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [speaking, setSpeaking] = useState(null);
  const [error, setError] = useState("");
  const runRef = useRef(0); // a newer read (or a stop) makes the old one's callbacks do nothing

  const stop = useCallback(() => {
    runRef.current += 1;
    if (supported) window.speechSynthesis.cancel();
    setSpeaking(null);
  }, [supported]);

  const speak = useCallback((key, text) => {
    setError("");
    if (!supported) {
      setError(NOT_SUPPORTED_READING);
      return;
    }
    window.speechSynthesis.cancel();
    const run = ++runRef.current;
    const parts = pieces(text);
    if (!parts.length) return;
    const voice = pickVoice();
    setSpeaking(key);
    parts.forEach((part, i) => {
      const u = new SpeechSynthesisUtterance(part);
      u.lang = voice ? voice.lang : LANG;
      if (voice) u.voice = voice;
      if (i === parts.length - 1) u.onend = () => runRef.current === run && setSpeaking(null);
      u.onerror = (e) => {
        if (runRef.current !== run || e.error === "interrupted" || e.error === "canceled") return;
        setSpeaking(null);
        setError("Couldn't read that aloud. Please try again.");
      };
      window.speechSynthesis.speak(u);
    });
  }, [supported]);

  useEffect(() => {
    if (!supported) return undefined;
    window.speechSynthesis.getVoices(); // voices load late in Chrome: ask early so they are ready on first tap
    return () => window.speechSynthesis.cancel();
  }, [supported]);

  const clearError = useCallback(() => setError(""), []);
  return { speaking, error, clearError, speak, stop };
}
